import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import type { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollDirective } from '@shared/ui';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SaveLabelLanguage } from '@telespot/domain';

import { LabelData } from '../../../state/label.state';

export interface OptionListItem {
  uuid: string;
  name: string;
  /**
   * Colour the option's label carries on the editing session's method type
   * (alpha included). When set, the colour action shows a swatch preview
   * instead of the palette icon; both faces are the same button.
   */
  color?: string;
  /**
   * Whether this option's label is not properly defined.
   */
  undefinedLabel: boolean;
}

@Component({
  selector: 'ts-category-options-management-list',
  templateUrl: './category-options-management-list.component.html',
  styleUrls: ['./category-options-management-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DragDropModule,
    OverlayModule,
    MatProgressSpinnerModule,
    InfiniteScrollDirective,
  ],
})
export class CategoryOptionsManagementListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: OptionListItem[] = [];
  @Input() categoryName = '';
  @Input() canEdit = true;
  /**
   * Whether the colour action is offered at all. Steps where label colour is
   * irrelevant (nothing ever renders it) hide the action instead of inviting
   * configuration that has no effect.
   */
  @Input() colorActionVisible = true;
  @Input() loading = true;
  @Input() searchTerm = '';
  @Input() suggestions: LabelData[] = [];
  @Input() suggestionsLoading = false;

  @Output() thresholdClicked = new EventEmitter<OptionListItem>();
  @Output() colorClicked = new EventEmitter<OptionListItem>();
  @Output() translateClicked = new EventEmitter<OptionListItem>();
  @Output() removeConfirmed = new EventEmitter<OptionListItem>();
  @Output() reordered = new EventEmitter<OptionListItem[]>();
  @Output() searchTermChanged = new EventEmitter<string>();
  @Output() optionPicked = new EventEmitter<LabelData>();
  @Output() freeTextSubmitted = new EventEmitter<string>();
  @Output() focused = new EventEmitter<string>();
  @Output() blurred = new EventEmitter<void>();
  @Output() loadMoreRequested = new EventEmitter<void>();

  @ViewChild('optionInput') private optionInputRef!: ElementRef<HTMLInputElement>;

  public readonly optionControl = new FormControl<string>('', { nonNullable: true });

  /**
   * Connected-overlay positions for the suggestion panel: prefer dropping below
   * the add-option row, fall back to opening upwards when there is no room. The
   * 4px offset mirrors the gap the panel used to get from its `margin-top`.
   */
  public readonly suggestionsOverlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  pendingRemovalUuid: string | null = null;
  public highlightedIndex: number | null = null;
  public isDropdownOpen = false;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.optionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((term) => {
      this.isDropdownOpen = true;
      this.searchTermChanged.emit(term ?? '');
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm']) {
      const next = this.searchTerm ?? '';
      if (this.optionControl.value !== next) {
        this.optionControl.setValue(next, { emitEvent: false });
      }
      this.highlightedIndex = null;
    }
    if (changes['suggestions']) {
      this.highlightedIndex = null;
    }
    if (changes['canEdit']) {
      if (this.canEdit) {
        this.optionControl.enable({ emitEvent: false });
      } else {
        this.optionControl.disable({ emitEvent: false });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByUuid(_: number, item: { uuid: string }): string {
    return item.uuid;
  }

  requestRemove(item: OptionListItem): void {
    this.pendingRemovalUuid = item.uuid;
  }

  confirmRemove(item: OptionListItem): void {
    this.pendingRemovalUuid = null;
    this.removeConfirmed.emit(item);
  }

  cancelRemove(): void {
    this.pendingRemovalUuid = null;
  }

  onDrop(event: CdkDragDrop<OptionListItem[]>): void {
    if (!this.canEdit) return;
    if (event.previousIndex === event.currentIndex) return;

    const next = [...this.items];
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    this.reordered.emit(next);
  }

  onPickSuggestion(label: LabelData): void {
    this.optionPicked.emit(label);
    this.optionInputRef.nativeElement.blur();
  }

  onSubmitFreeText(): void {
    const term = (this.optionControl.value ?? '').trim();
    if (!term) return;
    this.freeTextSubmitted.emit(term);
    this.optionInputRef.nativeElement.blur();
  }

  onFocus(): void {
    const value = (this.optionControl.value ?? '').trim();
    if (!value) return;
    this.isDropdownOpen = true;
    this.focused.emit(this.optionControl.value);
  }

  onBlur(): void {
    this.isDropdownOpen = false;
    this.highlightedIndex = null;
    this.blurred.emit();
  }

  onArrowDown(event: Event): void {
    if (!this.isDropdownOpen || this.searchTerm.length === 0) return;
    event.preventDefault();
    const count = this.suggestions.length + 1;
    this.highlightedIndex =
      this.highlightedIndex === null ? 0 : Math.min(this.highlightedIndex + 1, count - 1);
  }

  onArrowUp(event: Event): void {
    if (this.highlightedIndex === null) return;
    event.preventDefault();
    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
  }

  onEscape(): void {
    this.optionInputRef.nativeElement.blur();
  }

  onEnter(): void {
    if (
      this.highlightedIndex !== null &&
      this.highlightedIndex < this.suggestions.length
    ) {
      this.onPickSuggestion(this.suggestions[this.highlightedIndex]);
    } else {
      this.onSubmitFreeText();
    }
  }

  preferredName(label: LabelData): string {
    return label.languages.en ?? Object.values(label.languages).find((v): v is string => !!v) ?? '';
  }

  matchedLanguage(label: LabelData, term: string): string {
    const lower = (term ?? '').trim().toLowerCase();
    if (lower) {
      const entry = (Object.entries(label.languages) as [SaveLabelLanguage, string | undefined][])
        .find(([, v]) => typeof v === 'string' && v.toLowerCase().includes(lower));
      if (entry) return entry[0];
    }
    if (label.languages.en !== undefined) return 'en';
    const first = (Object.keys(label.languages) as SaveLabelLanguage[])[0];
    return first ?? 'en';
  }

  matchedValue(label: LabelData, term: string): string {
    const lower = (term ?? '').trim().toLowerCase();
    if (lower) {
      const entry = Object.values(label.languages).find(
        (v): v is string => typeof v === 'string' && v.toLowerCase().includes(lower)
      );
      if (entry) return entry;
    }
    return this.preferredName(label);
  }
}
